import { toggleObjectLock, deleteObject } from '~src/ui';
import { getGlobalControlsManager } from '../ControlsManager';
import {
  editorMoveObjectBackward,
  editorMoveObjectForward,
  editorBringObjectToFront,
  editorSendObjectToBack,
  onCloneObjects,
  resetTextRotation,
  adapt,
  toggleObjectShadow,
} from '~core';
import { AI_FEATURE_CATEGORIES, handleAiEdit } from '~core/aiEdit';
import { showAiFeatureInfo } from '~core/aiEdit/aiIndividualHelp';
import { removeBg, blurBg } from '~core/removebg';
import startExpand from '~core/expand/startExpand';
import { startMagicEraser } from '~core/magicEraser';
import handleSmile from '~core/smile/handleSmile';
import handleColorizeBw from '~core/colorize/handleColorizeBw';
import { startDissolvenza } from '~core/dissolvenza';
import { startChangeClothes } from '~core/changeClothes';
import { fetchAiCredits } from '~graphql/controllers';
import { handleBuyAiCredits } from '~src/sidebar/handleBuyAiCredits';
import { initAiCreditsInfoModal } from '~src/sidebar/initAiCreditsInfoModal';

// Define before usage to satisfy linting
export function createTransparencyControl(getTarget) {
  return {
    text: 'Trasparenza',
    svg: '/assets/svg/trasparenza.svg',
    className: 'btn-customize',
    children: [
      {
        type: 'control',
        className: 'control-row',
        render: (root, object) => {
          const row = document.createElement('div');
          row.className = 'floating-control-row';

          const iconWrap = document.createElement('div');
          iconWrap.className = 'control-icon';
          const img = document.createElement('img');
          img.className = 'inject-me no-fill';
          img.setAttribute('data-src', '/assets/svg/trasparenza.svg');
          iconWrap.appendChild(img);
          row.appendChild(iconWrap);

          const range = document.createElement('input');
          range.type = 'range';
          range.className = 'control-range';
          range.min = '0';
          range.max = '1';
          range.step = '0.01';
          const initial =
            typeof object.opacity === 'number' ? object.opacity : 1;
          range.value = String(initial);
          row.appendChild(range);

          const num = document.createElement('input');
          num.type = 'number';
          num.className = 'control-number';
          num.min = '0';
          num.max = '1';
          num.step = '0.01';
          num.value = String(initial);
          row.appendChild(num);

          const apply = (val, saveHistory = false) => {
            const v = Math.max(0, Math.min(1, parseFloat(val)));
            range.value = String(v);
            num.value = String(v);
            try {
              const obj = getTarget && getTarget();
              if (!obj) return;
              obj.set('opacity', v);
              obj.set('dirty', true);
              if (window.canvas) window.canvas.renderAll();
              if (saveHistory) {
                const { triggerStateChangedRequest } = require('~core');
                triggerStateChangedRequest();
              }
            } catch (e) {
              console.error('Failed injecting SVG for lock button', e);
            }
          };
          range.addEventListener('input', () => apply(range.value, false));
          range.addEventListener('change', () => apply(range.value, true));
          num.addEventListener('input', () => apply(num.value, false));
          num.addEventListener('change', () => apply(num.value, true));
          return row;
        },
      },
    ],
  };
}

// Build a reusable set of Object Actions buttons.
// getTarget: () => fabric.Object (the object to act on)
export function buildObjectActionsButtons(getTarget) {
  const withActive = (fn) => () => {
    const obj = getTarget && getTarget();
    if (obj && window.canvas) {
      window.canvas.setActiveObject(obj);
      window.canvas.requestRenderAll();
    }
    fn();
  };

  const isLocked = (obj) => !!(obj && obj.lockMovementX && obj.lockMovementY);

  const updateLockButtonDom = (el, locked) => {
    if (!el) return;
    try {
      const textEl = el.querySelector('.button-text');
      if (textEl) textEl.textContent = locked ? 'Sblocca' : 'Blocca';

      const iconWrap = el.querySelector('.button-icon');
      if (iconWrap) {
        iconWrap.innerHTML = '';
        const img = document.createElement('img');
        img.className = 'inject-me no-fill';
        img.setAttribute(
          'data-src',
          locked ? '/assets/svg/sblocca.svg' : '/assets/svg/blocca.svg'
        );
        iconWrap.appendChild(img);
        try {
          const { injectSvgIcons } = require('~src/ui');
          if (typeof injectSvgIcons === 'function')
            setTimeout(injectSvgIcons, 0);
        } catch (e) {
          console.error('Failed injecting SVG for lock button', e);
        }
      }

      el.classList.toggle('btn-unlock-pastel', locked);
    } catch (e) {
      console.error('Failed updating lock button DOM', e);
    }
  };

  const initialLocked = isLocked(getTarget && getTarget());

  const lockButton = {
    // Dynamic lock/unlock toggle
    text: initialLocked ? 'Sblocca' : 'Blocca',
    svg: initialLocked ? '/assets/svg/sblocca.svg' : '/assets/svg/blocca.svg',
    className: `btn-customize ${initialLocked ? 'btn-unlock-pastel' : ''}`,
    onClick: (event, _object, control) => {
      try {
        const obj = getTarget && getTarget();
        if (obj && window.canvas) {
          window.canvas.setActiveObject(obj);
          window.canvas.requestRenderAll();
        }
        toggleObjectLock();
        const nowLocked = isLocked(getTarget && getTarget());
        lockButton.text = nowLocked ? 'Sblocca' : 'Blocca';
        lockButton.svg = nowLocked
          ? '/assets/svg/sblocca.svg'
          : '/assets/svg/blocca.svg';
        lockButton.className = `btn-customize ${
          nowLocked ? 'btn-unlock-pastel' : ''
        }`;

        const el = event?.currentTarget;
        updateLockButtonDom(el, nowLocked);
        if (control && typeof control.refreshIconColors === 'function') {
          setTimeout(() => control.refreshIconColors(), 0);
        }
      } catch (err) {
        console.error('toggle lock failed', err);
      }
    },
  };

  return [
    lockButton,
    {
      text: 'Ombra',
      svg: '/assets/svg/img-shadow.svg',
      className: 'btn-customize',
      onClick: withActive(toggleObjectShadow),
    },
    createTransparencyControl(getTarget),
    {
      text: 'Livella Rotazione',
      svg: '/assets/svg/img-text-reset-rotation.svg',
      className: 'btn-customize',
      onClick: withActive(resetTextRotation),
    },
    {
      text: 'Adatta',
      svg: '/assets/svg/adatta.svg',
      className: 'btn-customize',
      onClick: withActive(adapt),
    },
    {
      text: 'Gira',
      svg: '/assets/svg/img-modify.svg',
      className: 'btn-customize',
      children: [
        {
          text: 'Capovolgi orizzontalmente',
          svg: '/assets/svg/capovolgi-orizzontalmente.svg',
          className: 'btn-customize',
          onClick: withActive(() => {
            const { editorFlipHorizontal } = require('~core');
            editorFlipHorizontal();
          }),
        },
        {
          text: 'Capovolgi verticalmente',
          svg: '/assets/svg/capovolgi-verticalmente.svg',
          className: 'btn-customize',
          onClick: withActive(() => {
            const { editorFlipVertical } = require('~core');
            editorFlipVertical();
          }),
        },
      ],
    },
    {
      text: 'Duplica',
      svg: '/assets/svg/duplica.svg',
      className: 'btn-customize',
      onClick: withActive(onCloneObjects),
    },
    {
      text: 'Livelli',
      svg: '/assets/svg/livello.svg',
      className: 'btn-customize',
      // Submenu with z-order options
      children: [
        {
          text: 'Sposta avanti',
          svg: '/assets/svg/livello-sposta-avanti.svg',
          className: 'btn-customize',
          onClick: withActive(editorMoveObjectForward),
          isEnabled: () => {
            try {
              const obj = getTarget && getTarget();
              const arr = (window.canvas && window.canvas.getObjects()) || [];
              const idx = arr.indexOf(obj);
              return idx !== -1 && idx < arr.length - 1;
            } catch (e) {
              return false;
            }
          },
        },
        {
          text: 'Sposta in primo piano',
          svg: '/assets/svg/livello-sposta-primo-piano.svg',
          className: 'btn-customize',
          onClick: withActive(editorBringObjectToFront),
          isEnabled: () => {
            try {
              const obj = getTarget && getTarget();
              const arr = (window.canvas && window.canvas.getObjects()) || [];
              const idx = arr.indexOf(obj);
              return idx !== -1 && idx < arr.length - 1;
            } catch (e) {
              return false;
            }
          },
        },
        {
          text: 'Sposta indietro',
          svg: '/assets/svg/livello-sposta-indietro.svg',
          className: 'btn-customize',
          onClick: withActive(editorMoveObjectBackward),
          isEnabled: () => {
            try {
              const obj = getTarget && getTarget();
              const arr = (window.canvas && window.canvas.getObjects()) || [];
              const idx = arr.indexOf(obj);
              return idx > 0;
            } catch (e) {
              return false;
            }
          },
        },
        {
          text: 'Sposta in secondo piano',
          svg: '/assets/svg/livello-sposta-secondo-piano.svg',
          className: 'btn-customize',
          onClick: withActive(editorSendObjectToBack),
          isEnabled: () => {
            try {
              const obj = getTarget && getTarget();
              const arr = (window.canvas && window.canvas.getObjects()) || [];
              const idx = arr.indexOf(obj);
              return idx > 0;
            } catch (e) {
              return false;
            }
          },
        },
      ],
    },
    {
      text: 'Rimuovi',
      svg: '/assets/svg/elimina.svg',
      className: 'btn-remove',
      onClick: withActive(deleteObject),
    },
  ];
}

// Flattened version for use as a hover submenu (no nested submenus)
export function buildObjectActionsSubmenu(getTarget) {
  const items = buildObjectActionsButtons(getTarget);
  const flat = [];
  items.forEach((it) => {
    if (Array.isArray(it.children) && it.children.length) {
      it.children.forEach((ch) => flat.push(ch));
    } else {
      flat.push(it);
    }
  });
  return flat;
}

// duplicate createTransparencyControl removed (defined earlier)

// Create a color subview definition and its navigation button.
// Returns { navigateButton, viewDef }
export function createColorSubview({
  viewId,
  title,
  containerClass,
  backTo = 'home',
  showBack = true,
}) {
  return {
    navigateButton: {
      text: 'Cambia Colore',
      inlineSvg:
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M12 3c4.97 0 9 3.582 9 8 0 2.137-1.425 3.5-3.5 3.5h-1.75a.75.75 0 0 0 0 1.5h.75A2.5 2.5 0 1 1 14 19.5h-1c-4.97 0-9-3.582-9-8S7.03 3 12 3Zm-4 7a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm3-1a1 1 0 1 0 2 0 1 1 0 0 0-2 0Zm5 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm-4 2a1 1 0 1 0-2 0 1 1 0 0 0 2 0Z"/></svg>',
      className: 'btn-customize',
      navigateTo: viewId,
    },
    viewDef: {
      [viewId]: {
        title,
        showBack,
        backTo,
        buttons: [{ type: 'container', className: containerClass }],
      },
    },
  };
}

// Attach common populate hooks for color subview.
export function attachColorPopulateHooks(ctrl, viewId, populateFn) {
  const populate = () => setTimeout(populateFn, 0);
  // On initial mount
  populate();
  // On navigate to viewId
  if (typeof ctrl.addClickListener === 'function') {
    ctrl.addClickListener((ev) => {
      if (ev && ev.type === 'navigate' && ev.viewId === viewId) populate();
    });
  }
  // On show again
  if (ctrl && typeof ctrl.show === 'function') {
    const originalShow = ctrl.show.bind(ctrl);
    ctrl.show = () => {
      originalShow();
      populate();
    };
  }
}

// Reusable button: Intelligenza Artificiale (nested: Categories -> Features -> Tooltip/Info)
export function createAiButton(getTarget) {
  // Ensure active object stays selected while we run actions
  const withActive = (fn) => async () => {
    const target = getTarget && getTarget();
    if (target && window.canvas) {
      window.canvas.setActiveObject(target);
      window.canvas.requestRenderAll();
    }
    try {
      await fn();
    } finally {
      const cm = getGlobalControlsManager();
      cm && cm.showControl(target);
    }
  };

  // Trigger legacy delegated handlers by ID (for enhancement family)
  const triggerDelegatedClick = (id) => {
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('button');
      el.id = id;
      el.style.display = 'none';
      document.body.appendChild(el);
    }
    el.click();
  };

  const runFeature = async (feature) => {
    const hideControlsIfNeeded = () => {
      try {
        const { getGlobalControlsManager } = require('~core/customControls');
        const cm = getGlobalControlsManager && getGlobalControlsManager();
        cm && cm.hideAllControls();
      } catch (e) {
        console.error('Failed hiding controls before AI feature', e);
      }
    };
    switch (feature.id) {
      case 'removebg':
        return removeBg();
      case 'blurbg':
        return blurBg();
      case 'expand':
        hideControlsIfNeeded();
        return startExpand();
      case 'magic-eraser':
        hideControlsIfNeeded();
        return startMagicEraser();
      case 'change-clothes':
        hideControlsIfNeeded();
        return startChangeClothes();
      case 'colorizebw':
        return handleColorizeBw();
      case 'smile':
        return handleSmile();
      case 'dissolvenza':
        return startDissolvenza();
      case 'imageEnhancement':
        return triggerDelegatedClick('editor-menu-image-imageEnhancement');
      case 'faceEnhancement':
        return triggerDelegatedClick('editor-menu-image-faceEnhancement');
      case 'makeLookYounger':
        return triggerDelegatedClick('editor-menu-image-makeLookYounger');
      default:
        if (feature.prompt) return handleAiEdit(feature.prompt);
        console.warn('No handler for AI feature', feature);
    }
  };

  // Build nested children: categories -> features
  const buildFeatureItem = (feature) => ({
    text: feature.title,
    className: 'btn-customize',
    onClick: withActive(() => runFeature(feature)),
    children: [
      // Tooltip-like preview as a non-interactive submenu item
      { type: 'preview', src: feature.image, alt: feature.title },
      {
        text: 'Info',
        svg: '/assets/svg/img-info.svg',
        className: 'btn-customize',
        onClick: () => showAiFeatureInfo(feature.id),
      },
    ],
  });

  const categoryIcon = {
    background: '🖼️',
    quality: '✨',
    corrections: '🪄',
    face: '💄',
  };
  const cleanLabel = (title) => {
    try {
      // Strip leading emoji/symbols and space, keep the readable label
      return String(title).replace(/^[^\p{L}\p{N}]+\s*/u, '');
    } catch (e) {
      // Fallback: remove first non-word block + space
      return String(title).replace(/^[^A-Za-z0-9]+\s*/, '');
    }
  };
  const categoryButtons = AI_FEATURE_CATEGORIES.map((cat) => ({
    text: cleanLabel(cat.title),
    icon: categoryIcon[cat.id] || undefined,
    className: 'btn-customize',
    children: cat.features.map(buildFeatureItem),
  }));

  // Credits submenu: parent label shows balance, submenu has actions only
  const creditsSubmenu = [
    {
      text: 'Acquista',
      className: 'btn-customize',
      onClick: async () => {
        try {
          await handleBuyAiCredits();
        } catch (e) {
          console.error('Open buy credits failed', e);
        }
      },
    },
    {
      text: 'Come funzionano?',
      className: 'btn-customize',
      onClick: async () => {
        try {
          initAiCreditsInfoModal();
          // Open modal if available
          const $ = window.jQuery || window.$;
          if ($) $('#aiCreditsInfoModal').modal('show');
        } catch (e) {
          console.error('Open credits info failed', e);
        }
      },
    },
    {
      text: 'Storico',
      className: 'btn-customize',
      onClick: () => {
        try {
          window.open('/credits', '_blank');
        } catch (e) {
          console.error('Open credits history failed', e);
        }
      },
    },
  ];

  const aiButtonConfig = {
    text: 'Intelligenza Artificiale',
    inlineSvg:
      '<svg width="24" height="24" viewBox="0 0 32 32" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M23.568 16.49c-1.808-.595-3.284-1.453-4.46-2.672-1.13-1.171-1.917-2.61-2.479-4.31-.575 1.75-1.39 3.204-2.548 4.375-1.176 1.19-2.639 2.019-4.395 2.599 1.802.588 3.275 1.435 4.448 2.642 1.139 1.172 1.928 2.615 2.49 4.329.576-1.75 1.391-3.202 2.548-4.37 1.177-1.19 2.64-2.015 4.396-2.593Zm-6.306 9.48a.657.657 0 0 1-.636.517.657.657 0 0 1-.643-.55c-1.067-5.2-3.357-7.717-8.69-8.784a.667.667 0 0 1 0-1.333c4.785-.982 7.228-3.12 8.418-7.612l.002-.01c.102-.385.195-.789.28-1.21a.657.657 0 0 1 .776-.486.663.663 0 0 1 .489.48c1.066 5.2 3.368 7.771 8.701 8.838a.667.667 0 0 1 0 1.333c-5.2 1.067-7.63 3.484-8.697 8.818Zm9.892-17.553c.11-.07.224-.132.343-.188a3.62 3.62 0 0 1 .925-.292.2.2 0 0 0 .2-.203c0-.113-.083-.187-.194-.208l-.031-.008a2.032 2.032 0 0 1-.053-.013 4.83 4.83 0 0 1-.829-.297 3.963 3.963 0 0 1-.363-.194 3.237 3.237 0 0 1-1.27-1.39 3.618 3.618 0 0 1-.273-.825l-.005-.026a4.401 4.401 0 0 1-.014-.075.204.204 0 0 0-.204-.204v.004c-.112 0-.187.083-.208.194l-.007.03a1.733 1.733 0 0 1-.014.054 4.828 4.828 0 0 1-.295.827c-.06.128-.125.25-.195.365a3.237 3.237 0 0 1-1.391 1.27 3.621 3.621 0 0 1-.926.292.204.204 0 0 0-.204.204h.005c0 .112.083.187.193.208l.031.007.043.01.01.003c.3.079.575.178.828.296.127.06.249.125.365.195a3.237 3.237 0 0 1 1.27 1.391 3.618 3.618 0 0 1 .272.824l.005.026.015.076a.204.204 0 0 0 .203.203v-.004c.113 0 .187-.083.208-.193l.008-.031a2.254 2.254 0 0 1 .013-.054c.078-.298.177-.574.296-.827.06-.127.124-.249.194-.365a3.239 3.239 0 0 1 1.05-1.082ZM9.53 25.917c.076-.047.155-.089.239-.128a3.245 3.245 0 0 1 .79-.254.29.29 0 1 0 0-.58 6.041 6.041 0 0 1-.766-.288 3.787 3.787 0 0 1-.26-.135 2.605 2.605 0 0 1-1.097-1.163 3.246 3.246 0 0 1-.253-.79.29.29 0 0 0-.581 0 5.22 5.22 0 0 1-.288.766 3.76 3.76 0 0 1-.135.258 2.604 2.604 0 0 1-1.163 1.098 3.239 3.239 0 0 1-.79.253.29.29 0 0 0 0 .581l.124.039c.231.074.445.156.641.25.09.042.177.087.26.134a2.604 2.604 0 0 1 1.097 1.163 3.242 3.242 0 0 1 .254.79.29.29 0 0 0 .58 0l.04-.123c.073-.232.156-.446.249-.642.042-.09.087-.177.135-.26.236-.412.534-.734.924-.97ZM11.115 8.47c-1.254.472-2.1 1.207-2.586 2.454a5.944 5.944 0 0 0-.227.722 8.187 8.187 0 0 0-.168.888.287.287 0 0 1-.266.266.287.287 0 0 1-.267-.266 9.144 9.144 0 0 0-.168-.888 5.944 5.944 0 0 0-.227-.722C6.72 9.677 5.874 8.942 4.62 8.47a8.384 8.384 0 0 0-.813-.257 12.674 12.674 0 0 0-.873-.195.287.287 0 0 1-.267-.267c0-.133.134-.267.267-.267l.104-.01c.284-.027.552-.064.804-.111.29-.055.561-.123.812-.205C5.86 6.764 6.62 6.044 7.089 4.934a5.84 5.84 0 0 0 .122-.314c.088-.247.164-.512.23-.796a9.878 9.878 0 0 0 .16-.89c0-.134.133-.267.267-.267.133 0 .266.133.266.267a10.962 10.962 0 0 0 .16.89c.066.284.142.549.23.796.038.108.079.212.122.314.468 1.11 1.228 1.83 2.435 2.224.251.082.521.15.812.205a9.128 9.128 0 0 0 .908.121c.133 0 .267.134.267.267a.287.287 0 0 1-.267.267 13.245 13.245 0 0 0-.873.195c-.288.076-.56.161-.813.257Z"></path></svg>',
    className: 'btn-customize',
    // Structure: categories..., divider, credits
    children: [
      ...categoryButtons,
      { type: 'divider' },
      {
        text: 'Crediti (…) ',
        className: 'btn-customize btn-ai-credits',
        children: [
          // Hidden control used to update parent label with current balance
          {
            type: 'control',
            className: 'ai-credits-updater',
            render: (root) => {
              try {
                root.style.display = 'none';
                fetchAiCredits()
                  .then((credits) => {
                    const total =
                      (credits?.edges?.[0]?.freeCreditsAmount || 0) +
                      (credits?.edges?.[0]?.paidCreditsAmount || 0);
                    // parent button is the previousSibling of the submenu container
                    const submenu = root && root.parentElement; // .floating-submenu
                    const parentBtn = submenu && submenu.previousSibling; // .floating-button
                    const label =
                      parentBtn && parentBtn.querySelector('.button-text');
                    if (label) label.textContent = `Crediti (${total})`;
                  })
                  .catch(() => {});
              } catch (e) {
                console.error('Failed updating slider fill (opacity)', e);
              }
              return root;
            },
          },
          ...creditsSubmenu,
        ],
      },
    ],
  };

  return aiButtonConfig;
}

// Reusable button: Strumenti e Regolazioni
export function createToolsButton(getTarget) {
  const withActive = (fn) => () => {
    const obj = getTarget && getTarget();
    if (obj && window.canvas) {
      window.canvas.setActiveObject(obj);
      window.canvas.requestRenderAll();
    }
    fn();
  };
  return {
    text: 'Strumenti',
    inlineSvg:
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" fill-rule="evenodd" d="M6.1 17.25a3 3 0 0 1 5.8 0h8.85a.75.75 0 1 1 0 1.5h-8.84a3 3 0 0 1-5.82 0H3.25a.75.75 0 1 1 0-1.5h2.84zm6-6a3 3 0 0 1 5.8 0h2.85a.75.75 0 1 1 0 1.5h-2.84a3 3 0 0 1-5.82 0H3.25a.75.75 0 1 1 0-1.5h8.84zm-6-6a3 3 0 0 1 5.8 0h8.85a.75.75 0 1 1 0 1.5h-8.84a3 3 0 0 1-5.82 0H3.25a.75.75 0 0 1 0-1.5h2.84zM9 7.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm6 6a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm-6 6a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z"></path></svg>',
    className: 'btn-customize',
    // Migrate Ritaglia / Riduci dimensioni / Gomma to floating controls
    children: [
      {
        text: 'Ritaglia',
        svg: '/assets/svg/crop.svg',
        className: 'btn-customize',
        onClick: withActive(() => {
          try {
            // Use existing listeners: simulate click on the bound control
            const btn = document.getElementById('editor-menu-image-crop');
            if (btn) btn.click();
          } catch (e) {
            console.error('start crop failed', e);
          }
        }),
      },
      {
        text: 'Riduci dimensioni',
        svg: '/assets/svg/resize.svg',
        className: 'btn-customize',
        onClick: withActive(async () => {
          try {
            const {
              showResizeImageDialog,
            } = require('~core/resizeImage/showResizeImageDialog');
            if (typeof showResizeImageDialog === 'function')
              await showResizeImageDialog();
          } catch (e) {
            console.error('resize dialog failed', e);
          }
        }),
      },
      {
        text: 'Gomma',
        svg: '/assets/svg/eraser.svg',
        className: 'btn-customize',
        onClick: withActive(() => {
          try {
            const { toggleObjectEraser } = require('~core');
            if (typeof toggleObjectEraser === 'function') toggleObjectEraser();
          } catch (e) {
            console.error('toggle eraser failed', e);
          }
        }),
      },
    ],
  };
}

// Migrate Filters panel to floating controls
export function createFiltersButton(getTarget) {
  const withActive = (fn) => () => {
    const obj = getTarget && getTarget();
    if (obj && window.canvas) {
      window.canvas.setActiveObject(obj);
      window.canvas.requestRenderAll();
    }
    fn();
  };
  return {
    text: 'Filtri',
    // Inline SVG to ensure immediate black icon; avoid async injection race.
    inlineSvg:
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M3 6.25A1.25 1.25 0 0 1 4.25 5h15.5A1.25 1.25 0 0 1 21 6.25v.5A1.25 1.25 0 0 1 19.75 8H4.25A1.25 1.25 0 0 1 3 6.75v-.5Zm3 5A1.25 1.25 0 0 1 7.25 10h9.5A1.25 1.25 0 0 1 18 11.25v.5A1.25 1.25 0 0 1 16.75 13h-9.5A1.25 1.25 0 0 1 6 11.75v-.5Zm3 5A1.25 1.25 0 0 1 10.25 15h6.5A1.25 1.25 0 0 1 18 16.25v.5A1.25 1.25 0 0 1 16.75 18h-6.5A1.25 1.25 0 0 1 9 16.75v-.5Z"/></svg>',
    className: 'btn-customize',
    children: (() => {
      try {
        const { default: editorConfig } = require('~config/editorConfig');
        // Italian localization + capitalization for filter names
        const itLabels = {
          'remove-all': 'Originale',
          grayscale: 'Bianco e Nero',
          sepia: 'Seppia',
          brownie: 'Brownie',
          vintage: 'Vintage',
          kodachrome: 'Kodachrome',
          technicolor: 'Technicolor',
          polaroid: 'Polaroid',
        };
        const capitalize = (str) =>
          String(str)
            .split(/[\s_-]+/)
            .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ''))
            .join(' ');
        return editorConfig.filters.map((filter) => {
          const pretty = itLabels[filter.name] || capitalize(filter.name);
          return {
            text: pretty,
            className: 'btn-customize',
            onClick: withActive(() => {
              try {
                let activeObject = getTarget && getTarget();
                if (!activeObject) return;
                if (activeObject.type === 'MagicFrame') {
                  activeObject = activeObject.editingImg;
                  if (!activeObject) return;
                }
                if (filter.name === 'remove-all') {
                  activeObject.filters = [];
                } else if (filter.filterFunc) {
                  const newFilter = filter.filterFunc();
                  if (filter.type && !newFilter.type)
                    newFilter.type = filter.type;
                  // Replace all with selected filter (consistent with sidebar behaviour)
                  activeObject.filters = [newFilter];
                }
                activeObject.applyFilters();
                window.canvas && window.canvas.renderAll();
                try {
                  const { triggerStateChangedRequest } = require('~core');
                  if (typeof triggerStateChangedRequest === 'function')
                    triggerStateChangedRequest();
                } catch (e) {
                  console.error(
                    'Failed updating number input fill (gamma RGB)',
                    e
                  );
                }
              } catch (e) {
                console.error('apply filter failed', e);
              }
            }),
          };
        });
      } catch (e) {
        console.error('build filters failed', e);
        return [];
      }
    })(),
  };
}

// New: Regolazioni submenu with hover controls
export function createAdjustmentsButton(getTarget) {
  const applyFilterValue = (name, value) => {
    try {
      let obj = getTarget && getTarget();
      if (!obj) return;
      // Route MagicFrame to its editing image
      let image = obj;
      if (obj.type === 'MagicFrame') {
        image = obj.editingImg;
      }
      if (!image) return;
      const { advancedFilters } = require('~config/editorConfig').default;
      const cfg = advancedFilters.find((f) => f.name === name);
      if (!cfg) return;
      const idx = (image.filters || []).findIndex(
        (f) => f && f.type === cfg.type
      );
      const fNew = cfg.filterFunc(parseFloat(value));
      if (!Array.isArray(image.filters)) image.filters = [];
      if (idx >= 0) image.filters[idx] = fNew;
      else image.filters.push(fNew);
      image.applyFilters();
      if (window.canvas) window.canvas.renderAll();
      try {
        const { triggerStateChangedRequestDebounced } = require('~core');
        if (typeof triggerStateChangedRequestDebounced === 'function')
          triggerStateChangedRequestDebounced();
      } catch (e) {
        console.error('Failed triggering debounced state change (filters)', e);
      }
    } catch (e) {
      console.error('applyFilterValue failed', e);
    }
  };

  const getFilterValue = (name) => {
    try {
      let obj = getTarget && getTarget();
      if (!obj) return 0;
      let image = obj;
      if (obj.type === 'MagicFrame') image = obj.editingImg;
      if (!image) return 0;
      const { advancedFilters } = require('~config/editorConfig').default;
      const cfg = advancedFilters.find((f) => f.name === name);
      if (!cfg) return 0;
      const f = (image.filters || []).find((x) => x && x.type === cfg.type);
      if (!f) return 0;
      switch (cfg.type) {
        case 'Exposure':
          return parseFloat(f.exposure) || 0;
        case 'ShadowBoost':
          return parseFloat(f.shadowBoost) || 0;
        case 'Brightness':
          return parseFloat(f.brightness) || 0;
        case 'Contrast':
          return parseFloat(f.contrast) || 0;
        case 'Saturation':
          return parseFloat(f.saturation) || 0;
        case 'Blur':
          return parseFloat(f.blur) || 0;
        default:
          return 0;
      }
    } catch (e) {
      return 0;
    }
  };

  const makeControl = ({
    name,
    svg,
    label,
    min,
    max,
    step,
    stepNumber,
    defaultValue = 0,
  }) => {
    return {
      text: label,
      svg,
      className: 'btn-customize',
      // Nested control on hover
      children: [
        {
          type: 'control',
          className: 'control-row',
          render: () => {
            // Build: icon | range | number
            const row = document.createElement('div');
            row.className = 'floating-control-row';

            const iconWrap = document.createElement('div');
            iconWrap.className = 'control-icon';
            const img = document.createElement('img');
            img.className = 'inject-me no-fill';
            img.setAttribute('data-src', svg);
            iconWrap.appendChild(img);
            row.appendChild(iconWrap);

            const range = document.createElement('input');
            range.type = 'range';
            range.className = 'control-range';
            range.min = String(min);
            range.max = String(max);
            range.step = String(step);
            const initial = getFilterValue(name);
            range.value = String(
              isNaN(initial)
                ? defaultValue
                : Math.min(max, Math.max(min, initial))
            );
            row.appendChild(range);

            // Visual fill for active portion (WebKit/Chromium via background gradient)
            const setRangeFill = (el) => {
              try {
                const mn = parseFloat(el.min || '0');
                const mx = parseFloat(el.max || '1');
                const vl = parseFloat(el.value || '0');
                const pct = Math.max(
                  0,
                  Math.min(100, ((vl - mn) / (mx - mn)) * 100)
                );
                el.style.background = `linear-gradient(to right, #0f172a 0%, #0f172a ${pct}%, #e2e8f0 ${pct}%, #e2e8f0 100%)`;
              } catch (e) {
                console.error(
                  'Failed updating slider fill after value change',
                  e
                );
              }
            };
            setRangeFill(range);

            const num = document.createElement('input');
            num.type = 'number';
            num.className = 'control-number';
            num.min = String(min);
            num.max = String(max);
            num.step = String(stepNumber != null ? stepNumber : step);
            // Align number precision with control requirements
            const _decimalsMap = { brightness: 2, contrast: 2, saturation: 2 };
            const _dp = _decimalsMap[name];
            num.value =
              _dp != null
                ? Number(range.value).toFixed(_dp)
                : String(range.value);
            row.appendChild(num);

            // Event wiring: keep in sync and apply
            const apply = (v) => {
              const val = Math.min(max, Math.max(min, parseFloat(v)));
              range.value = String(val);
              num.value = _dp != null ? val.toFixed(_dp) : String(val);
              setRangeFill(range);
              applyFilterValue(name, val);
            };
            range.addEventListener('input', () => apply(range.value));
            range.addEventListener('change', () => apply(range.value));
            num.addEventListener('input', () => apply(num.value));
            num.addEventListener('change', () => apply(num.value));

            return row;
          },
        },
      ],
    };
  };

  // Effects matching sidebar ranges and icons
  const items = [
    // Colore (RGB sliders + magic wand)
    {
      text: 'Colore',
      svg: '/assets/svg/tavolozza.svg',
      className: 'btn-customize',
      children: (() => {
        // Shared RGB state initialized from current image gamma
        const getImageForAdjust = () => {
          let obj = getTarget && getTarget();
          if (!obj) return null;
          if (obj.type === 'MagicFrame') return obj.editingImg || null;
          return obj;
        };
        const getGamma = () => {
          try {
            const image = getImageForAdjust();
            if (!image) return [1, 1, 1];
            const gammaFilter = (image.filters || []).find(
              (f) => f && typeof f === 'object' && 'gamma' in f
            );
            if (gammaFilter && Array.isArray(gammaFilter.gamma)) {
              return [
                parseFloat(gammaFilter.gamma[0]) || 1,
                parseFloat(gammaFilter.gamma[1]) || 1,
                parseFloat(gammaFilter.gamma[2]) || 1,
              ];
            }
          } catch (e) {
            console.error('Failed reading gamma from image', e);
          }
          return [1, 1, 1];
        };
        const rgbState = (() => {
          const [r, g, b] = getGamma();
          return { red: r, green: g, blue: b };
        })();

        const applyGamma = () => {
          try {
            const image = getImageForAdjust();
            if (!image) return;
            const Gamma =
              (window.fabric &&
                window.fabric.Image &&
                window.fabric.Image.filters &&
                window.fabric.Image.filters.Gamma) ||
              null;
            if (!Gamma) return;
            const newGamma = new Gamma({
              gamma: [rgbState.red, rgbState.green, rgbState.blue],
              type: 'Gamma',
            });
            const idx = (image.filters || []).findIndex(
              (f) => f && typeof f === 'object' && 'gamma' in f
            );
            if (!Array.isArray(image.filters)) image.filters = [];
            if (idx >= 0) image.filters[idx] = newGamma;
            else image.filters.push(newGamma);
            image.applyFilters();
            if (window.canvas) window.canvas.renderAll();
            try {
              const { triggerStateChangedRequestDebounced } = require('~core');
              if (typeof triggerStateChangedRequestDebounced === 'function')
                triggerStateChangedRequestDebounced();
            } catch (e) {
              console.error('Failed debounced trigger after gamma apply', e);
            }
          } catch (e) {
            console.error('applyGamma failed', e);
          }
        };

        const buildRgbRow = (key, label, svg) => ({
          type: 'control',
          className: 'control-row',
          render: () => {
            const row = document.createElement('div');
            row.className = 'floating-control-row';
            const iconWrap = document.createElement('div');
            iconWrap.className = 'control-icon';
            const img = document.createElement('img');
            img.className = 'inject-me no-fill';
            img.setAttribute('data-src', svg);
            iconWrap.appendChild(img);
            row.appendChild(iconWrap);
            const range = document.createElement('input');
            range.type = 'range';
            range.className = 'control-range';
            range.min = '0.01';
            range.max = '2.20';
            range.step = '0.01';
            range.value = String(rgbState[key] || 1);
            row.appendChild(range);
            const num = document.createElement('input');
            num.type = 'number';
            num.className = 'control-number';
            num.min = '0.01';
            num.max = '2.20';
            num.step = '0.01';
            num.value = Number(range.value).toFixed(2);
            row.appendChild(num);

            const setRangeFill = (el) => {
              try {
                const mn = parseFloat(el.min || '0.01');
                const mx = parseFloat(el.max || '2.20');
                const vl = parseFloat(el.value || '1');
                const pct = Math.max(
                  0,
                  Math.min(100, ((vl - mn) / (mx - mn)) * 100)
                );
                el.style.background = `linear-gradient(to right, #0f172a 0%, #0f172a ${pct}%, #e2e8f0 ${pct}%, #e2e8f0 100%)`;
              } catch (e) {
                console.error('Failed updating slider fill (gamma RGB row)', e);
              }
            };
            setRangeFill(range);

            const apply = (val) => {
              const v = Math.max(0.01, Math.min(2.2, parseFloat(val)));
              rgbState[key] = v;
              range.value = String(v);
              num.value = v.toFixed(2);
              setRangeFill(range);
              applyGamma();
            };
            range.addEventListener('input', () => apply(range.value));
            range.addEventListener('change', () => apply(range.value));
            num.addEventListener('input', () => apply(num.value));
            num.addEventListener('change', () => apply(num.value));
            return row;
          },
        });

        return [
          buildRgbRow('red', 'Rosso', '/assets/svg/img-text-interlinea.svg'),
          buildRgbRow('green', 'Verde', '/assets/svg/img-text-interlinea.svg'),
          buildRgbRow('blue', 'Blu', '/assets/svg/img-text-interlinea.svg'),
          {
            text: 'Rimuovi Colore Selettivo',
            svg: '/assets/svg/eraser.svg',
            className: 'btn-customize',
            onClick: () => {
              try {
                const {
                  initMagicWand,
                  magicWandManager,
                } = require('~core/magicWand');
                if (typeof initMagicWand === 'function') initMagicWand();
                if (
                  magicWandManager &&
                  typeof magicWandManager.startMagicWand === 'function'
                ) {
                  magicWandManager.startMagicWand();
                }
              } catch (e) {
                console.error('magic wand start failed', e);
              }
            },
          },
        ];
      })(),
    },
    makeControl({
      name: 'exposure',
      label: 'Esposizione',
      svg: '/assets/svg/img-exposure.svg',
      min: -1,
      max: 1,
      step: 0.01,
      defaultValue: 0,
    }),
    makeControl({
      name: 'shadowBoost',
      label: 'Ombre',
      svg: '/assets/svg/img-shadowBoost.svg',
      min: 0,
      max: 1,
      step: 0.01,
      defaultValue: 0,
    }),
    createTransparencyControl(getTarget),
    makeControl({
      name: 'blur',
      label: 'Sfocatura',
      svg: '/assets/svg/img-blur.svg',
      min: 0,
      max: 1,
      step: 0.005,
      defaultValue: 0,
    }),
    makeControl({
      name: 'brightness',
      label: 'Luminosità',
      svg: '/assets/svg/img-luminosita.svg',
      min: -1,
      max: 1,
      step: 0.01,
      stepNumber: 0.01,
      defaultValue: 0,
    }),
    makeControl({
      name: 'contrast',
      label: 'Contrasto',
      svg: '/assets/svg/img-contrasto-img.svg',
      min: -1,
      max: 1,
      step: 0.01,
      stepNumber: 0.01,
      defaultValue: 0,
    }),
    makeControl({
      name: 'saturation',
      label: 'Saturazione',
      svg: '/assets/svg/img-saturazione-img.svg',
      min: -1,
      max: 1,
      step: 0.01,
      stepNumber: 0.01,
      defaultValue: 0,
    }),
  ];

  // Append Filtri submenu as last item under Regolazioni
  try {
    const filtersSubmenu = createFiltersButton(getTarget);
    if (filtersSubmenu) items.push(filtersSubmenu);
  } catch (e) {
    console.error('Failed appending Filtri submenu to Regolazioni', e);
  }

  return {
    text: 'Regolazioni',
    svg: '/assets/svg/regolazioni.svg',
    className: 'btn-customize',
    // Hover submenu with effect items
    children: items,
  };
}
